import { MicroSessionTile } from "@components/film/MicroSessionTile"
import { Button } from "@components/ui/Button"
import { DropdownMenu } from "@components/ui/DropdownMenu"
import { DropdownItem } from "@components/ui/menu-helpers"
import { FerveCartItem } from "@features/ferve/ferve-cart"
import { PurchasedTicket } from "@features/ferve/ferve-wishlist"
import { useFerve } from "@features/ferve/FerveProvider"
import { flyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { trackEvent } from "@features/tracking/tracking"
import { TrackingHelpers } from "@features/tracking/tracking-helpers"
import { EventType, FilmSessionFragment, FilmTileFragment } from "@generated-types"
import { useIsMobile } from "@hooks/useIsMobile"
import { formatEndTime, formatSimpleTime } from "@utils/date-format"
import { isPastEvent } from "@utils/past-event"
import { cn, tv } from "@utils/tw"
import { Link, useRouter } from "eddev/routing"
import { useMemo, useRef } from "react"
import { usePlannerSharing } from "./usePlannerSharing"
import { AnimatePresence, motion } from "motion/react"
import { CheckIcon } from "@heroicons/react/24/outline"
import { downloadIcsFile } from "@utils/ics-file"
import { PlannerItem as PlannerItemData } from "@features/ferve/useResolvedPlanner"

export function PlannerItem(item: PlannerItemData) {
  const sharing = usePlannerSharing()
  const ferve = useFerve()
  const ref = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const wishlist = useFerve().wishlist
  const isMobile = useIsMobile()

  const isPast = useMemo(() => {
    return isPastEvent({ session: item.session })
  }, [item.session])

  const isSelected = sharing.selectedItems.has(item.session.info?.ferveId!)

  const styles = itemStyles({
    isPast: isPast && !sharing.isActive,
    selectable: sharing.isActive,
    clashes: item.clashWith?.previous || item.clashWith?.next,
    clashDirection: item.clashWith?.previous ? "previous" : item.clashWith?.next ? "next" : undefined,
    selected: sharing.isActive && isSelected,
  })

  const dropdownItems: DropdownItem[] = [
    {
      type: "item",
      icon: "ticket",
      label: "Buy Tickets",
      onSelect: () => {
        router.navigate(item.film.uri! + "#tickets")
        trackEvent("Watchlist: Buy Tickets", {}, ref.current)
      },
    },
    {
      type: "item",
      icon: "externalLink",
      label: item.film.eventInfo?.eventType === EventType.Film ? "View Film" : "View Session",
      onSelect: () => {
        router.navigate(item.film.uri!)
        trackEvent("Watchlist: View Session", {}, ref.current)
      },
    },
    {
      type: "item",
      icon: "calendar",
      label: "Add to Calendar",
      onSelect: () => {
        trackEvent("Watchlist: Add to Calendar", {}, ref.current)
        downloadIcsFile(
          [
            {
              film: item.film,
              session: item.session,
            },
          ],
          item.film.title || "My Watchlist",
        )
      },
    },
  ]

  if (item.purchasedTickets) {
    dropdownItems.push(
      {
        type: "separator",
      },
      {
        type: "item",
        icon: "download",
        label: "Tickets PDF",
        onSelect: () => {
          trackEvent("Watchlist: PDF Tickets", {}, ref.current)
          window.open(
            ferve.url(
              "/Account/TicketsPDF/?hash=&group=false" +
                item.purchasedTickets?.barcodes.map((b) => "&hashes=" + b.hash).join(""),
            ),
            "_blank",
          )
        },
      },
      {
        type: "item",
        icon: "qr",
        label: "Present Tickets",
        onSelect: () => {
          trackEvent("Watchlist: Mobile Tickets", {}, ref.current)
          window.open(
            ferve.url(
              "/Account/TicketsMobile/?hash=&group=false" +
                item.purchasedTickets?.barcodes.map((b) => "&hashes=" + b.hash).join(""),
            ),
            "_blank",
          )
        },
      },
    )
    if (isMobile) {
      dropdownItems.push({
        type: "item",
        icon: "wallet",
        label: "Add to Wallet",
        onSelect: () => {
          trackEvent("Watchlist: Cancel Tickets", {}, ref.current)
          window.open(
            ferve.url(
              "/Account/Wallet/?hash=&group=false" +
                item.purchasedTickets?.barcodes.map((b) => "&hashes=" + b.hash).join(""),
            ),
            "_blank",
          )
        },
      })
    }
  }
  if (item.cartItem) {
    dropdownItems.push(
      {
        type: "separator",
      },
      {
        type: "item",
        icon: "cart",
        label: "Open Cart",
        onSelect: () => {
          flyingCarpet.launch("cart")
          trackEvent("Watchlist: Open Cart", {}, ref.current)
        },
      },
    )
  }
  if (!item.purchasedTickets && !item.cartItem) {
    dropdownItems.push(
      {
        type: "separator",
      },
      {
        type: "item",
        icon: "cross",
        label: "Remove",
        onSelect: () => {
          wishlist.remove(item.session.info?.ferveId!)
          trackEvent("Watchlist: Remove", {}, ref.current)
        },
      },
    )
  }

  return (
    <div
      ref={ref}
      className={styles.base()}
      data-mixpanel-area="Wishlist Item Actions"
      {...(item.film && TrackingHelpers.filmAttrs(item.film))}
      onClick={() => {
        if (sharing.isActive) {
          sharing.toggleItem(item.session.info?.ferveId!)
        }
      }}
    >
      {/* Sharing selection checkbox */}
      <AnimatePresence initial={false}>
        {sharing.isActive && (
          <motion.div
            key="checkbox"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "auto" }}
            exit={{ opacity: 0, width: 0 }}
            className="w-[24px] flex items-center justify-center"
          >
            <div className="flex-none size-[16px] ml-4 mr-1 rounded-full bg-transparent border border-orange text-orange">
              <CheckIcon
                className={cn(
                  "block size-[100%] transition-all",
                  isSelected ? "scale-100 opacity-100" : "opacity-0 scale-50",
                )}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Link */}
      <Link key="link" href={item.film.uri} className={styles.inner()} title={item.film.title!}>
        <div className={styles.times()}>
          <span className="leading-none">{formatSimpleTime(item.session?.info?.dateTime!)}</span>
          <span className="type-label-s text-fg-secondary leading-none">
            {formatEndTime(item.session?.info?.dateTime!, item.film.eventInfo?.runtime ?? 0)}
          </span>
        </div>
        <div className={styles.line()} />
        <div className={styles.item()}>
          <MicroSessionTile
            film={item.film}
            session={item.session}
            showDate={false}
            showVenue
            isInCart={!!item.cartItem}
            isPurchased={!!item.purchasedTickets}
            inPlannerPanel
          />
        </div>
      </Link>
      {/* Actions Button */}
      <AnimatePresence initial={false}>
        {!sharing.isActive && (
          <motion.div
            key="actions"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "auto" }}
            exit={{ opacity: 0, width: 0 }}
            className="w-min"
          >
            <div className={styles.actions()}>
              <DropdownMenu
                trigger={<Button shape="icon" icon="ellipsis" variant="ghost" />}
                align="end"
                items={dropdownItems}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const itemStyles = tv({
  base: "relative flex border border-transparent items-stretch text-fg py-[0.8em] type-ui-s rounded-md md:hover:bg-bg-low/50 has-[[data-state='open']]:bg-bg-low transition-colors my-[3px]",
  slots: {
    inner: "flex gap-[1em] flex-1 items-stretch",
    times: "flex flex-col gap-[0.7em] text-right w-[4.7em] flex-none justify-center",
    line: "w-[2.5px] bg-fg rounded-full flex-none relative",
    item: "flex-1",
    actions: "flex gap-2 pr-[0.8em]",
  },
  variants: {
    isPast: {
      true: {
        line: "bg-bg-mid",
        times: "text-fg-secondary",
        item: "opacity-50",
      },
    },
    clashes: {
      true: {
        line: "bg-coral",
      },
    },
    clashDirection: {
      previous: {
        line: "-mt-4 rounded-t-none",
      },
      next: {
        line: "-mb-4 rounded-b-none",
      },
    },
    selectable: {
      true: {
        base: "border border-bg-low hover:border-bg-mid hover:bg-transparent cursor-pointer",
        inner: "pointer-events-none",
      },
    },
    selected: {
      true: {
        base: "!border-orange !bg-orange/10",
      },
    },
  },
})
